import {
  mapValidatedWindowRangeToSourceAnchors,
  type CharacterExtractionScope,
  type PreparedCharacterExtractionValidationIssue
} from "./extractionPreparation.js";
import {
  groundSingleWitnessCoreferenceChains,
  prepareSingleWitnessCoreferenceChainExtractionCall,
  projectGroundedCoreferenceChainsToSourceAnchors,
  validatePreparedCoreferenceChainExtractionCall,
  validateSingleWitnessCoreferenceChainOutput,
  type CoreferenceChainGroundingDiagnostic,
  type CoreferenceChainSourceAnchorProjection,
  type GroundSingleWitnessCoreferenceChainsOptions,
  type GroundedSingleWitnessCoreferenceChainOutput,
  type PrepareSingleWitnessCoreferenceChainExtractionOptions,
  type PreparedSingleWitnessCoreferenceChainExtractionCall,
  type SingleWitnessCoreferenceChainOutput,
  type SingleWitnessCoreferenceChainValidationIssue
} from "./coreferenceChainExtraction.js";
import type {
  CharacterIndexAlignedRowInput,
  CharacterIndexInput,
  CharacterIndexSourceAnchor
} from "./types.js";

export const DEFAULT_COREFERENCE_CHAIN_MAX_ROWS_PER_WINDOW = 12;
export const DEFAULT_COREFERENCE_CHAIN_OVERLAP_ROWS = 2;

export interface PrepareSingleWitnessCoreferenceChainWindowOptions
  extends PrepareSingleWitnessCoreferenceChainExtractionOptions {
  readonly maxRowsPerWindow?: number;
  readonly overlapRows?: number;
}

export interface CoreferenceChainWindowSourceScope {
  readonly kind: CharacterExtractionScope["kind"];
  readonly witnessId: string;
  readonly sceneId?: string;
  readonly requestedRowIds?: readonly string[];
}

export interface CoreferenceChainWindowSourceRow {
  readonly rowId: string;
  readonly rowLabel: string;
}

export interface CoreferenceChainWindowRowLabelRange {
  readonly start: string;
  readonly end: string;
}

export interface CoreferenceChainWindowMetadata {
  readonly windowId: string;
  readonly windowIndex: number;
  readonly windowCount: number;
  readonly sourceScope: CoreferenceChainWindowSourceScope;
  readonly rowLabelRange: CoreferenceChainWindowRowLabelRange | null;
  readonly sourceRowIds: readonly string[];
  readonly sourceRows: readonly CoreferenceChainWindowSourceRow[];
  readonly overlapsPrevious: boolean;
  readonly overlapsNext: boolean;
}

export interface PreparedSingleWitnessCoreferenceChainWindowCall
  extends PreparedSingleWitnessCoreferenceChainExtractionCall {
  readonly window: CoreferenceChainWindowMetadata;
}

export interface GroundedSingleWitnessCoreferenceChainWindow {
  readonly window: CoreferenceChainWindowMetadata;
  readonly validationIssues: readonly SingleWitnessCoreferenceChainValidationIssue[];
  readonly preparedCallIssues: readonly PreparedCharacterExtractionValidationIssue[];
  readonly grounded: GroundedSingleWitnessCoreferenceChainOutput;
  readonly diagnostics: readonly CoreferenceChainGroundingDiagnostic[];
}

export interface CoreferenceChainWindowDiagnosticEntry {
  readonly windowId: string;
  readonly windowIndex: number;
}

export interface CoreferenceChainWindowValidationDiagnostic extends CoreferenceChainWindowDiagnosticEntry {
  readonly issue: SingleWitnessCoreferenceChainValidationIssue;
}

export interface CoreferenceChainWindowGroundingDiagnostic extends CoreferenceChainWindowDiagnosticEntry {
  readonly diagnostic: CoreferenceChainGroundingDiagnostic;
}

export interface CoreferenceChainWindowDiagnosticSummary {
  readonly windowCount: number;
  readonly validationIssues: readonly CoreferenceChainWindowValidationDiagnostic[];
  readonly groundingDiagnostics: readonly CoreferenceChainWindowGroundingDiagnostic[];
}

export interface CoreferenceChainWindowSourceAnchorProjection {
  readonly windowId: string;
  readonly windowIndex: number;
  readonly sourceAnchor: CoreferenceChainSourceAnchorProjection;
}

export interface CoreferenceChainRecallRowDiagnostic {
  readonly rowId: string;
  readonly rowLabel: string;
  readonly windowIds: readonly string[];
}

export interface CoreferenceChainRecallGroundingDiagnostic {
  readonly windowId: string;
  readonly windowIndex: number;
  readonly diagnostic: CoreferenceChainGroundingDiagnostic;
  readonly rowIds: readonly string[];
  readonly rowLabels: readonly string[];
}

export interface CoreferenceChainWindowRecallDiagnostics {
  readonly rowsWithZeroGroundedMentions: readonly CoreferenceChainRecallRowDiagnostic[];
  readonly rowsWithGroundingDiagnostics: readonly CoreferenceChainRecallGroundingDiagnostic[];
}

interface EligibleCoreferenceRow {
  readonly rowId: string;
  readonly rowLabel: string;
  readonly sceneId?: string | null;
}

const getScopeWitnessId = (scope: CharacterExtractionScope): string => scope.witnessId;

const normalizeWindowSize = (value: number | undefined, fallback: number, name: string): number => {
  const normalized = value === undefined ? fallback : Math.floor(value);
  if (!Number.isFinite(normalized) || normalized < 1) {
    throw new Error(`${name} must be a positive integer.`);
  }
  return normalized;
};

const normalizeOverlapRows = (value: number | undefined, maxRowsPerWindow: number): number => {
  const normalized = value === undefined ? DEFAULT_COREFERENCE_CHAIN_OVERLAP_ROWS : Math.floor(value);
  if (!Number.isFinite(normalized) || normalized < 0) {
    throw new Error("overlapRows must be a non-negative integer.");
  }
  if (normalized >= maxRowsPerWindow) {
    throw new Error("overlapRows must be smaller than maxRowsPerWindow.");
  }
  return normalized;
};

const rowMatchesScope = (
  row: CharacterIndexAlignedRowInput,
  scope: CharacterExtractionScope,
  requestedRowIdSet: ReadonlySet<string> | null
): boolean => {
  if (scope.kind === "witness") {
    return true;
  }
  if (scope.kind === "scene") {
    return row.sceneId === scope.sceneId;
  }
  return requestedRowIdSet?.has(row.rowId) || false;
};

const rowHasUsableWitnessCell = (
  row: CharacterIndexAlignedRowInput,
  witnessId: string
): boolean => (
  row.cells.some((cell) => (
    cell.witnessId === witnessId &&
    !cell.gap &&
    cell.text.trim().length > 0
  ))
);

const collectEligibleRows = (
  input: CharacterIndexInput,
  scope: CharacterExtractionScope
): EligibleCoreferenceRow[] => {
  const witnessId = getScopeWitnessId(scope);
  if (!input.witnesses.some((witness) => witness.witnessId === witnessId)) {
    throw new Error(`Cannot prepare coreference windows: unknown witness ${witnessId}.`);
  }

  const requestedRowIdSet = scope.kind === "rows"
    ? new Set(scope.rowIds.map((rowId) => rowId.trim()).filter(Boolean))
    : null;

  return input.alignedRows
    .filter((row) => rowMatchesScope(row, scope, requestedRowIdSet))
    .filter((row) => rowHasUsableWitnessCell(row, witnessId))
    .map((row) => ({
      rowId: row.rowId,
      rowLabel: row.rowLabel,
      ...(row.sceneId !== undefined ? { sceneId: row.sceneId } : {})
    }));
};

const buildSourceScope = (scope: CharacterExtractionScope): CoreferenceChainWindowSourceScope => ({
  kind: scope.kind,
  witnessId: scope.witnessId,
  ...(scope.kind === "scene" ? { sceneId: scope.sceneId } : {}),
  ...(scope.kind === "rows" ? { requestedRowIds: [...scope.rowIds] } : {})
});

const buildWindowId = (
  sourceScope: CoreferenceChainWindowSourceScope,
  rows: readonly EligibleCoreferenceRow[],
  windowIndex: number
): string => {
  const firstRowId = rows[0]?.rowId || "empty";
  const lastRowId = rows.at(-1)?.rowId || "empty";
  const scopePart = sourceScope.kind === "scene"
    ? `scene-${sourceScope.sceneId}`
    : sourceScope.kind;
  return [
    "coref-window",
    sourceScope.witnessId,
    scopePart,
    String(windowIndex + 1).padStart(4, "0"),
    firstRowId,
    lastRowId
  ].join(":");
};

const buildWindowMetadata = (
  sourceScope: CoreferenceChainWindowSourceScope,
  rows: readonly EligibleCoreferenceRow[],
  windowIndex: number,
  windowCount: number,
  overlapsPrevious: boolean,
  overlapsNext: boolean
): CoreferenceChainWindowMetadata => ({
  windowId: buildWindowId(sourceScope, rows, windowIndex),
  windowIndex,
  windowCount,
  sourceScope,
  rowLabelRange: rows.length > 0
    ? {
        start: rows[0]!.rowLabel,
        end: rows.at(-1)!.rowLabel
      }
    : null,
  sourceRowIds: rows.map((row) => row.rowId),
  sourceRows: rows.map((row) => ({
    rowId: row.rowId,
    rowLabel: row.rowLabel
  })),
  overlapsPrevious,
  overlapsNext
});

const chunkRows = (
  rows: readonly EligibleCoreferenceRow[],
  maxRowsPerWindow: number,
  overlapRows: number
): EligibleCoreferenceRow[][] => {
  if (rows.length === 0) {
    return [];
  }

  const windows: EligibleCoreferenceRow[][] = [];
  const step = maxRowsPerWindow - overlapRows;
  for (let start = 0; start < rows.length; start += step) {
    const end = Math.min(rows.length, start + maxRowsPerWindow);
    windows.push(rows.slice(start, end));
    if (end === rows.length) {
      break;
    }
  }
  return windows;
};

export const prepareSingleWitnessCoreferenceChainExtractionWindowCalls = (
  input: CharacterIndexInput,
  scope: CharacterExtractionScope,
  options: PrepareSingleWitnessCoreferenceChainWindowOptions = {}
): PreparedSingleWitnessCoreferenceChainWindowCall[] => {
  const maxRowsPerWindow = normalizeWindowSize(
    options.maxRowsPerWindow,
    DEFAULT_COREFERENCE_CHAIN_MAX_ROWS_PER_WINDOW,
    "maxRowsPerWindow"
  );
  const overlapRows = normalizeOverlapRows(options.overlapRows, maxRowsPerWindow);
  const sourceScope = buildSourceScope(scope);
  const eligibleRows = collectEligibleRows(input, scope);
  const rowWindows = chunkRows(eligibleRows, maxRowsPerWindow, overlapRows);

  return rowWindows.map((rows, windowIndex) => {
    const preparedCall = prepareSingleWitnessCoreferenceChainExtractionCall(
      input,
      {
        kind: "rows",
        witnessId: scope.witnessId,
        rowIds: rows.map((row) => row.rowId)
      },
      {
        promptTextBuilder: options.promptTextBuilder,
        promptVersion: options.promptVersion
      }
    );
    const metadata = buildWindowMetadata(
      sourceScope,
      rows,
      windowIndex,
      rowWindows.length,
      windowIndex > 0 && overlapRows > 0,
      windowIndex < rowWindows.length - 1 && overlapRows > 0
    );

    return {
      ...preparedCall,
      window: metadata
    };
  });
};

const isSingleWitnessCoreferenceChainOutput = (
  output: unknown
): output is SingleWitnessCoreferenceChainOutput => (
  validateSingleWitnessCoreferenceChainOutput(output).length === 0
);

export const groundSingleWitnessCoreferenceChainWindowResponse = (
  call: PreparedSingleWitnessCoreferenceChainWindowCall,
  output: unknown,
  options: GroundSingleWitnessCoreferenceChainsOptions = {}
): GroundedSingleWitnessCoreferenceChainWindow => {
  const validationIssues = validateSingleWitnessCoreferenceChainOutput(output);
  const preparedCallIssues = validatePreparedCoreferenceChainExtractionCall(call);
  const grounded = validationIssues.length === 0 && isSingleWitnessCoreferenceChainOutput(output)
    ? groundSingleWitnessCoreferenceChains(call, output, options)
    : { characters: [], diagnostics: [] };

  return {
    window: call.window,
    validationIssues,
    preparedCallIssues,
    grounded,
    diagnostics: grounded.diagnostics
  };
};

export const aggregateCoreferenceChainWindowDiagnostics = (
  groundedWindows: readonly GroundedSingleWitnessCoreferenceChainWindow[]
): CoreferenceChainWindowDiagnosticSummary => ({
  windowCount: groundedWindows.length,
  validationIssues: groundedWindows.flatMap((groundedWindow) => (
    groundedWindow.validationIssues.map((issue) => ({
      windowId: groundedWindow.window.windowId,
      windowIndex: groundedWindow.window.windowIndex,
      issue
    }))
  )),
  groundingDiagnostics: groundedWindows.flatMap((groundedWindow) => (
    groundedWindow.diagnostics.map((diagnostic) => ({
      windowId: groundedWindow.window.windowId,
      windowIndex: groundedWindow.window.windowIndex,
      diagnostic
    }))
  ))
});

export const projectGroundedCoreferenceChainWindowSourceAnchors = (
  groundedWindows: readonly GroundedSingleWitnessCoreferenceChainWindow[]
): CoreferenceChainWindowSourceAnchorProjection[] => (
  groundedWindows.flatMap((groundedWindow) => (
    projectGroundedCoreferenceChainsToSourceAnchors(groundedWindow.grounded)
      .map((sourceAnchor) => ({
        windowId: groundedWindow.window.windowId,
        windowIndex: groundedWindow.window.windowIndex,
        sourceAnchor
      }))
  ))
);

const unique = <T>(values: readonly T[]): T[] => Array.from(new Set(values));

const rowMapFromWindows = (
  windows: readonly PreparedSingleWitnessCoreferenceChainWindowCall[]
): Map<string, { rowLabel: string; windowIds: string[] }> => {
  const rowMap = new Map<string, { rowLabel: string; windowIds: string[] }>();

  for (const window of windows) {
    for (const row of window.window.sourceRows) {
      const existing = rowMap.get(row.rowId);
      if (existing) {
        existing.windowIds.push(window.window.windowId);
      } else {
        rowMap.set(row.rowId, {
          rowLabel: row.rowLabel,
          windowIds: [window.window.windowId]
        });
      }
    }
  }

  return rowMap;
};

const mapCandidateRangesToRowAnchors = (
  call: PreparedSingleWitnessCoreferenceChainWindowCall,
  ranges: readonly { readonly start: number; readonly end: number }[]
): CharacterIndexSourceAnchor[] => (
  ranges.flatMap((range) => {
    try {
      return mapValidatedWindowRangeToSourceAnchors(call, range);
    } catch {
      return [];
    }
  })
);

export const diagnoseCoreferenceChainWindowRecall = (
  windows: readonly PreparedSingleWitnessCoreferenceChainWindowCall[],
  groundedWindows: readonly GroundedSingleWitnessCoreferenceChainWindow[]
): CoreferenceChainWindowRecallDiagnostics => {
  const rowMap = rowMapFromWindows(windows);
  const groundedRowIds = new Set(
    groundedWindows.flatMap((groundedWindow) => (
      groundedWindow.grounded.characters.flatMap((character) => (
        character.chain.flatMap((mention) => mention.sourceAnchors.map((anchor) => anchor.rowId))
      ))
    ))
  );
  const preparedWindowById = new Map(windows.map((window) => [window.window.windowId, window]));

  return {
    rowsWithZeroGroundedMentions: Array.from(rowMap.entries())
      .filter(([rowId]) => !groundedRowIds.has(rowId))
      .map(([rowId, row]) => ({
        rowId,
        rowLabel: row.rowLabel,
        windowIds: unique(row.windowIds)
      })),
    rowsWithGroundingDiagnostics: groundedWindows.flatMap((groundedWindow) => {
      const preparedWindow = preparedWindowById.get(groundedWindow.window.windowId);
      if (!preparedWindow) {
        return [];
      }

      return groundedWindow.grounded.characters.flatMap((character) => (
        character.chain.flatMap((mention) => {
          if (mention.diagnostics.length === 0) {
            return [];
          }

          const anchors = mention.contextSourceAnchors.length > 0
            ? mention.contextSourceAnchors
            : mapCandidateRangesToRowAnchors(preparedWindow, mention.contextCandidateRanges);
          const rowIds = unique(anchors.map((anchor) => anchor.rowId));
          const rowLabels = rowIds.map((rowId) => rowMap.get(rowId)?.rowLabel || rowId);

          return mention.diagnostics.map((diagnostic) => ({
            windowId: groundedWindow.window.windowId,
            windowIndex: groundedWindow.window.windowIndex,
            diagnostic,
            rowIds,
            rowLabels
          }));
        })
      ));
    })
  };
};
